/**
 * Décor : ciel, étoiles, poussière de vitesse, rochers, secteurs, balises, station, parois de limite.
 * Les rochers utilisent la même géométrie mise à l'échelle que leur collision (demi-axes de arena.json).
 * Zones (phase D) : rochers, secteurs et station lointaine de chaque zone sont construits au chargement sous
 * un nœud par zone ; `setZone` montre celui de la zone courante et change le ciel.
 */
import { assetUrl } from '../assets';
import {
  Color3,
  DirectionalLight,
  PBRMaterial,
  Scene,
  EquiRectangularCubeTexture,
  HDRFiltering,
  HemisphericLight,
  LinesMesh,
  Mesh,
  MeshBuilder,
  Quaternion,
  StandardMaterial,
  Texture,
  TransformNode,
  Vector3,
  VertexData,
} from '@babylonjs/core';
import { zoneData, type ArenaData, type ArtData, type CameraData, type GameData, type ZoneData } from '../data';
import { rockVariantIndex } from '../sim/world/collision';
import type { ModelLibrary } from './models';
import { SurfaceDetailPlugin, type SurfaceDetailSet } from './surfaceDetail';
import { buildGround, buildPlanet, type PlanetView } from './planet';


export class Environment {
  private readonly dust: LinesMesh;
  private readonly dustPositions: Float32Array;
  private readonly dustOffsets: Float32Array;
  private readonly boundaryPlanes: { mesh: Mesh; axis: 'x' | 'y' | 'z'; sign: number }[] = [];
  private readonly beacons: Mesh[] = [];
  private readonly sectorMarkers: { mesh: Mesh; baseScale: number }[] = [];
  readonly rocks: TransformNode[] = [];
  /** Décor propre à chaque zone (rochers, secteurs, station lointaine). */
  private readonly zoneRoots = new Map<string, TransformNode>();
  private arena: ArenaData;
  private skyMaterial!: StandardMaterial;
  private readonly skyTextures = new Map<string, Texture>();
  private readonly envTextures = new Map<string, EquiRectangularCubeTexture>();
  zone = '';
  private stars!: Mesh;
  private readonly planets = new Map<string, PlanetView>();
  /** Orages ioniques : prochain éclair, éclair en cours. */
  private storm = { next: 2, bolt: null as LinesMesh | null, left: 0 };
  private time = 0;
  private static readonly DUST_COUNT = 700;
  private static readonly DUST_EXTENT = 160;

  constructor(
    private readonly scene: Scene,
    private readonly data: GameData,
    private readonly models: ModelLibrary,
    private readonly rockHullCount: number,
    private readonly art: ArtData,
    private readonly streaks: CameraData['speedStreaks'],
    private readonly lights: SceneLights,
    private readonly detail: SurfaceDetailSet,
  ) {
    this.arena = data.arena;
    this.buildSky();
    this.buildStars();
    const dust = this.buildDust();
    this.dust = dust.mesh;
    this.dustPositions = dust.positions;
    this.dustOffsets = dust.offsets;
    for (const id of data.zones.order) {
      const root = new TransformNode(`zone-${id}`, scene);
      const arena = zoneData(data, id).arena;
      this.buildRocks(arena, root);
      this.buildSectors(arena, root);
      this.buildStation(arena, root);
      const zone = data.zones.zones[id]!;
      if (zone.planet) this.planets.set(id, buildPlanet(scene, zone.planet, root));
      if (zone.ground) {
        const detail = new Texture(assetUrl('textures/ground_detail.jpg'), scene, false, true, Texture.TRILINEAR_SAMPLINGMODE);
        detail.anisotropicFilteringLevel = 8;
        buildGround(scene, zone.ground, detail, root);
      }
      this.zoneRoots.set(id, root);
    }
    this.buildBeacons();
    this.buildBoundary();
    this.setZone(data.zones.home);
  }

  /** Montre le décor d'une zone, son ciel et son éclairage d'ambiance. */
  setZone(id: string): void {
    const zone: ZoneData | undefined = this.data.zones.zones[id];
    if (!zone || id === this.zone) return;
    this.zone = id;
    this.arena = zoneData(this.data, id).arena;
    for (const [zoneId, root] of this.zoneRoots) root.setEnabled(zoneId === id);
    let tex = this.skyTextures.get(zone.sky.texture);
    if (!tex) {
      tex = new Texture(assetUrl(zone.sky.texture), this.scene, true, false);
      tex.coordinatesMode = Texture.FIXED_EQUIRECTANGULAR_MODE;
      this.skyTextures.set(zone.sky.texture, tex);
    }
    // Fond atténué : la zone de combat reste lisible devant la nébuleuse.
    tex.level = this.art.skyLevel * zone.sky.level;
    this.skyMaterial.reflectionTexture = tex;
    // Le ciel sert aussi d'éclairage d'environnement aux matériaux PBR (reflets, ambiance).
    // Préfiltré (réflexions floutées selon la rugosité) : sans cela, toute surface reflète le ciel comme un miroir.
    let env = this.envTextures.get(zone.sky.texture);
    if (!env) {
      const tex: EquiRectangularCubeTexture = new EquiRectangularCubeTexture(assetUrl(zone.sky.texture), this.scene, 256, false, true, () => {
        try {
          void new HDRFiltering(this.scene.getEngine(), { hdrScale: 1 }).prefilter(tex).catch(() => undefined);
        } catch {
          // Préfiltrage indisponible (moteur limité) : réflexions brutes.
        }
      });
      env = tex;
      this.envTextures.set(zone.sky.texture, env);
    }
    this.scene.environmentTexture = env;
    this.lights.setZone(zone);
    // Brouillard (nébuleuse, atmosphère), étoiles masquées sous une atmosphère.
    if (zone.fog) {
      this.scene.fogMode = Scene.FOGMODE_EXP2;
      this.scene.fogColor = new Color3(...zone.fog.color);
      this.scene.fogDensity = zone.fog.density;
    } else {
      this.scene.fogMode = Scene.FOGMODE_NONE;
    }
    this.stars.setEnabled(zone.stars !== false);
    this.storm.bolt?.dispose();
    this.storm.bolt = null;
  }

  private buildSky(): void {
    this.scene.environmentIntensity = this.art.render.environmentIntensity;
    const sky = MeshBuilder.CreateSphere('sky', { diameter: 50000, segments: 32, sideOrientation: Mesh.BACKSIDE }, this.scene);
    const mat = new StandardMaterial('sky', this.scene);
    this.skyMaterial = mat;
    mat.disableLighting = true;
    mat.diffuseColor = Color3.Black();
    mat.specularColor = Color3.Black();
    mat.emissiveColor = new Color3(0.0, 0.0, 0.0);
    mat.backFaceCulling = false;
    sky.material = mat;
    sky.infiniteDistance = true;
    sky.isPickable = false;
    sky.applyFog = false;
  }

  /** Étoiles fixes à l'infini : profondeur et repères de rotation. */
  private buildStars(): void {
    const count = 2600;
    const positions: number[] = [];
    const colors: number[] = [];
    let seed = 12345;
    const rand = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
    for (let i = 0; i < count; i++) {
      const u = rand() * 2 - 1, theta = rand() * Math.PI * 2;
      const r = Math.sqrt(1 - u * u);
      const d = 20000;
      positions.push(r * Math.cos(theta) * d, u * d, r * Math.sin(theta) * d);
      const b = 0.45 + rand() * 0.55;
      const warm = rand();
      colors.push(b * (0.8 + warm * 0.2), b * 0.88, b * (1 - warm * 0.25), 1);
    }
    const stars = new Mesh('stars', this.scene);
    const vd = new VertexData();
    vd.positions = positions;
    vd.colors = colors;
    vd.applyToMesh(stars);
    const mat = new StandardMaterial('stars', this.scene);
    mat.pointsCloud = true;
    mat.pointSize = 2;
    mat.disableLighting = true;
    mat.emissiveColor = Color3.White();
    stars.material = mat;
    stars.infiniteDistance = true;
    stars.isPickable = false;
    stars.applyFog = false;
    this.stars = stars;
  }

  /** Poussière locale repliée autour de la caméra, dessinée en traits étirés par la vitesse (perception de vitesse). */
  private buildDust(): { mesh: LinesMesh; positions: Float32Array; offsets: Float32Array } {
    const n = Environment.DUST_COUNT;
    const e = Environment.DUST_EXTENT;
    const offsets = new Float32Array(n * 3);
    let seed = 777;
    const rand = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
    for (let i = 0; i < n * 3; i++) offsets[i] = rand() * e;
    const lines = Array.from({ length: n }, () => [Vector3.Zero(), new Vector3(0, 0, 0.01)]);
    const mesh = MeshBuilder.CreateLineSystem('dust', { lines, updatable: true }, this.scene);
    mesh.color = new Color3(0.55, 0.68, 0.85);
    mesh.alpha = 0.55;
    mesh.isPickable = false;
    mesh.alwaysSelectAsActiveMesh = true;
    return { mesh, positions: new Float32Array(n * 6), offsets };
  }

  private buildRocks(arena: ArenaData, parent: TransformNode): void {
    for (const obstacle of arena.obstacles) {
      // Même variante que la collision (rockVariantIndex) ; sans enveloppe extraite, sphère facettée = ellipsoïde.
      const index = rockVariantIndex(obstacle, this.rockHullCount);
      const key = index >= 0 && this.models.has(`rock_${index}`) ? (`rock_${index}` as const) : ('rock_fallback' as const);
      if (index >= 0 && key === 'rock_fallback') console.warn(`Modèle rock_${index} absent : rendu et collision divergent pour ${obstacle.id}.`);
      const node = this.models.instantiate(key, obstacle.id);
      node.position.set(...obstacle.position);
      node.rotationQuaternion = new Quaternion(...obstacle.rotation);
      // Modèle unitaire (dimension max 1) étiré aux diamètres de l'ellipsoïde de collision.
      node.scaling.set(obstacle.semiAxes[0] * 2, obstacle.semiAxes[1] * 2, obstacle.semiAxes[2] * 2);
      node.parent = parent;
      for (const mesh of node.getChildMeshes()) mesh.freezeWorldMatrix();
      this.rocks.push(node);
    }
  }

  private buildSectors(arena: ArenaData, parent: TransformNode): void {
    for (const sector of arena.sectors) {
      const ring = MeshBuilder.CreateTorus(`sector-${sector.id}`, { diameter: 34, thickness: 0.6, tessellation: 64 }, this.scene);
      ring.position.set(...sector.center);
      ring.parent = parent;
      const mat = new StandardMaterial(`sector-${parent.name}-${sector.id}`, this.scene);
      mat.disableLighting = true;
      mat.emissiveColor = new Color3(...sector.color);
      mat.alpha = 0.55;
      ring.material = mat;
      ring.isPickable = false;
      this.sectorMarkers.push({ mesh: ring, baseScale: 1 });
      const core = MeshBuilder.CreateSphere(`sector-core-${sector.id}`, { diameter: 3, segments: 8 }, this.scene);
      core.position.set(...sector.center);
      core.parent = parent;
      core.material = mat;
      core.isPickable = false;
    }
  }

  private buildBeacons(): void {
    const d = this.arena.beaconDistance;
    const mat = new StandardMaterial('beacon', this.scene);
    mat.disableLighting = true;
    mat.emissiveColor = new Color3(0.2, 0.75, 1);
    const spots = [[d, 0, 0], [-d, 0, 0], [0, d, 0], [0, -d, 0], [0, 0, d], [0, 0, -d]] as const;
    for (const [i, p] of spots.entries()) {
      const beacon = MeshBuilder.CreateSphere(`beacon-${i}`, { diameter: 9, segments: 8 }, this.scene);
      beacon.position.set(p[0], p[1], p[2]);
      beacon.material = mat;
      beacon.isPickable = false;
      this.beacons.push(beacon);
    }
  }

  /**
   * Station Vigie dans l'arène (roue Meshy + moyeu procédural, tools/assets/station_vigie_model.py). Le moyeu
   * reçoit le détail de surface des docks dans le repère de la station ; sans modèle, repli procédural à l'échelle.
   */
  private buildStation(arena: ArenaData, parent: TransformNode): void {
    if (!arena.station) return;
    const hasModel = this.models.has('vigie');
    const station = this.models.instantiate(hasModel ? 'vigie' : 'station', `station-${parent.name}`);
    station.position.set(...arena.station.position);
    station.rotationQuaternion = new Quaternion(...arena.station.rotation);
    station.parent = parent;
    if (!hasModel) {
      station.scaling.setAll(arena.station.span / 1500);
      return;
    }
    const invWorld = station.computeWorldMatrix(true).clone().invert();
    const d = this.art.dockDetail;
    for (const mesh of station.getChildMeshes()) {
      const material = mesh.material;
      if (!(material instanceof PBRMaterial) || material.pluginManager?.getPlugin('SurfaceDetail')) continue;
      material.environmentIntensity = 0.2;
      // Moyeu procédural seulement : la roue Meshy porte sa propre texture.
      if (!material.name.startsWith('vigie_') || material.name.endsWith('glow') || material.name === 'vigie_solar') continue;
      const plugin = new SurfaceDetailPlugin(material, this.detail, { hullTile: d.hullTile * 1.6, hangarTile: d.hangarTile, albedo: d.albedo * (material.name === 'vigie_trim' ? 0.3 : 0.8), normal: d.normal });
      plugin.invWorld.copyFrom(invWorld);
    }
  }

  private buildBoundary(): void {
    const h = this.arena.halfExtent;
    const mat = new StandardMaterial('boundary', this.scene);
    mat.disableLighting = true;
    mat.emissiveColor = new Color3(0.2, 0.75, 1);
    mat.wireframe = true;
    mat.backFaceCulling = false;
    const faces: [('x' | 'y' | 'z'), number][] = [['x', 1], ['x', -1], ['y', 1], ['y', -1], ['z', 1], ['z', -1]];
    for (const [axis, sign] of faces) {
      const plane = MeshBuilder.CreateGround(`boundary-${axis}${sign}`, { width: h * 2, height: h * 2, subdivisions: 40 }, this.scene);
      plane.material = mat;
      plane.isPickable = false;
      if (axis === 'x') { plane.rotation.z = Math.PI / 2; plane.position.x = sign * h; }
      if (axis === 'y') { plane.position.y = sign * h; }
      if (axis === 'z') { plane.rotation.x = Math.PI / 2; plane.position.z = sign * h; }
      plane.visibility = 0;
      this.boundaryPlanes.push({ mesh: plane, axis, sign });
    }
  }

  update(dt: number, cameraPosition: Vector3, playerPosition: Vector3, velocity: Vector3 = Vector3.Zero()): void {
    this.time += dt;
    this.lights.follow(cameraPosition, playerPosition);
    const planet = this.planets.get(this.zone);
    if (planet) {
      planet.body.rotation.y += planet.spin * dt;
      planet.clouds.rotation.y += planet.spin * 1.35 * dt;
    }
    this.updateStorm(dt, cameraPosition);
    // Poussière repliée dans un cube centré sur la caméra, étirée le long de la vitesse.
    const e = Environment.DUST_EXTENT;
    const half = e / 2;
    const p = this.dustPositions, o = this.dustOffsets;
    const cx = cameraPosition.x, cy = cameraPosition.y, cz = cameraPosition.z;
    const speed = velocity.length();
    const streak = this.streaks;
    const length = Math.min(streak.maxLength, Math.max(0.08, (speed - streak.startSpeed) * streak.lengthFactor));
    const dir = speed > 1e-3 ? velocity.scale(1 / speed) : Vector3.Zero();
    for (let i = 0, k = 0; i < o.length; i += 3, k += 6) {
      const x = cx + ((((o[i]! - cx) % e) + e) % e) - half;
      const y = cy + ((((o[i + 1]! - cy) % e) + e) % e) - half;
      const z = cz + ((((o[i + 2]! - cz) % e) + e) % e) - half;
      p[k] = x; p[k + 1] = y; p[k + 2] = z;
      p[k + 3] = x - dir.x * length; p[k + 4] = y - dir.y * length; p[k + 5] = z - dir.z * length;
    }
    this.dust.updateVerticesData('position', p, false, false);
    this.dust.alpha = Math.min(0.85, 0.35 + speed / 300);

    // Parois visibles dans les 200 derniers mètres.
    const h = this.arena.halfExtent;
    for (const { mesh, axis, sign } of this.boundaryPlanes) {
      const distance = h - playerPosition[axis] * sign;
      const t = 1 - Math.min(1, Math.max(0, distance / this.arena.boundaryWarning));
      mesh.visibility = t * t * 0.6;
      mesh.setEnabled(t > 0.001);
    }
    const blink = 0.6 + 0.4 * Math.sin(this.time * 2.4);
    for (const beacon of this.beacons) beacon.visibility = blink;
    for (const marker of this.sectorMarkers) {
      marker.mesh.rotation.y += dt * 0.25;
      marker.mesh.rotation.x = 0.4;
    }
  }

  /**
   * Orages ioniques de la nébuleuse : éclair ramifié à quelques centaines de mètres de la caméra, flash de
   * l'ambiance et du brouillard, à intervalles aléatoires.
   */
  private updateStorm(dt: number, camera: Vector3): void {
    const s = this.storm;
    const zone = this.data.zones.zones[this.zone];
    if (s.bolt) {
      s.left -= dt;
      s.bolt.visibility = Math.max(0, s.left / 0.25) * (0.6 + 0.4 * Math.sin(this.time * 90));
      if (s.left <= 0) {
        s.bolt.dispose();
        s.bolt = null;
        this.lights.flash(0);
        if (zone?.fog) this.scene.fogColor = new Color3(...zone.fog.color);
      }
    }
    if (!zone?.storms) return;
    s.next -= dt;
    if (s.next > 0 || s.bolt) return;
    s.next = 1.5 + Math.random() * 5;
    const dir = new Vector3(Math.random() - 0.5, (Math.random() - 0.5) * 0.4, Math.random() - 0.5).normalize();
    const start = camera.add(dir.scale(250 + Math.random() * 350)).add(new Vector3(0, 120, 0));
    const points = [start];
    let p = start;
    for (let i = 0; i < 12; i++) {
      p = p.add(new Vector3((Math.random() - 0.5) * 40, -22 - Math.random() * 18, (Math.random() - 0.5) * 40));
      points.push(p);
    }
    const bolt = MeshBuilder.CreateLines('storm-bolt', { points }, this.scene);
    bolt.color = new Color3(0.85, 0.75, 1);
    bolt.isPickable = false;
    bolt.applyFog = false;
    s.bolt = bolt;
    s.left = 0.25;
    this.lights.flash(1.6);
    if (zone.fog) this.scene.fogColor = new Color3(...zone.fog.color).scale(2.2);
  }

  setDustVisible(visible: boolean): void {
    this.dust.setEnabled(visible);
  }
}

/**
 * Lumières de la scène. Créées AVANT le chargement des modèles : les maillages sources des conteneurs glTF
 * (hors scène) ne relisent pas les lumières ajoutées après eux, et leurs copies en jeu en héritent ; sinon
 * vaisseaux et rochers ne sont éclairés que par le reflet du ciel.
 */
export class SceneLights {
  private readonly cameraLight: DirectionalLight;
  /** Soleil du décor : seule lumière qui porte des ombres (postfx.ts). */
  readonly key: DirectionalLight;
  private readonly fill: HemisphericLight;

  constructor(scene: Scene, private readonly art: ArtData) {
    // Lumière principale (Unreal : rotator(-35, -40, 0), intensité 5, teinte bleutée) et rebond doux.
    const key = new DirectionalLight('key', new Vector3(-0.47, -0.57, 0.67).normalize(), scene);
    this.key = key;
    key.intensity = art.keyLight;
    key.diffuse = new Color3(0.78, 0.86, 1);
    key.specular = new Color3(0.6, 0.65, 0.75);
    const bounce = new DirectionalLight('bounce', new Vector3(0.47, 0.5, -0.72).normalize(), scene);
    bounce.intensity = art.bounceLight;
    bounce.diffuse = new Color3(0.65, 0.76, 1);
    bounce.specular = Color3.Black();
    const fill = new HemisphericLight('fill', new Vector3(0, 1, 0), scene);
    this.fill = fill;
    fill.intensity = art.ambient.intensity;
    fill.diffuse = new Color3(...art.ambient.sky);
    fill.groundColor = new Color3(...art.ambient.ground);
    fill.specular = Color3.Black();
    // Lumière de personnage : orientée comme la caméra (un peu plongeante), mise à jour à chaque image.
    this.cameraLight = new DirectionalLight('camera-light', new Vector3(0, -0.4, -1).normalize(), scene);
    this.cameraLight.intensity = art.cameraLight.intensity;
    this.cameraLight.diffuse = new Color3(...art.cameraLight.color);
    this.cameraLight.specular = new Color3(...art.cameraLight.color).scale(0.35);
  }

  /** Teinte du soleil et de l'ambiance de la zone. */
  setZone(zone: ZoneData): void {
    this.key.diffuse = new Color3(...zone.keyColor);
    this.key.specular = new Color3(...zone.keyColor).scale(0.75);
    this.fill.diffuse = new Color3(...zone.ambientSky);
  }

  /** Éclair d'orage : surcroît d'ambiance (0 = retour à la normale). */
  flash(extra: number): void {
    this.fill.intensity = this.art.ambient.intensity * (1 + extra);
  }

  /** Oriente la lumière de personnage selon l'axe de visée (de la caméra vers ce qu'elle regarde). */
  follow(cameraPosition: Vector3, target: Vector3): void {
    const view = target.subtract(cameraPosition);
    if (view.lengthSquared() < 1e-6) return;
    view.normalize();
    const pitch = (this.art.cameraLight.pitchDeg * Math.PI) / 180;
    this.cameraLight.direction.copyFrom(view.scale(Math.cos(pitch)).add(new Vector3(0, -Math.sin(pitch), 0)).normalize());
  }
}
